import React from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedTabItem<T extends string> {
  value: T;
  /** Label from tablet up, e.g. "Protected · 2". */
  label: string;
  /** Optional shorter label on mobile, e.g. "Protected". */
  shortLabel?: string;
}

interface SegmentedTabsProps<T extends string> {
  items: SegmentedTabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name for the group, e.g. "Filter products". */
  label: string;
  className?: string;
}

/** Pill-style filter tabs (Figma "Tabs"). Full width on mobile, compact from tablet up. */
function SegmentedTabs<T extends string>({ items, value, onChange, label, className }: SegmentedTabsProps<T>) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = items[(index + step + items.length) % items.length];
    onChange(next.value);
    (e.currentTarget.parentElement?.children[(index + step + items.length) % items.length] as HTMLElement)?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className={cn('tw-flex tw-gap-1 tw-rounded-xl tw-bg-ds-subtle tw-p-1', className)}>
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              'tw-flex tw-flex-1 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-[10px] tw-px-2 tw-py-[9px] tw-text-[14px] tw-leading-5 tw-transition-colors md:tw-flex-none md:tw-px-4',
              'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand',
              selected
                ? 'tw-bg-ds-surface tw-font-semibold tw-text-ds-text-primary tw-shadow-ds-raised'
                : 'tw-font-medium tw-text-ds-text-secondary hover:tw-text-ds-text-primary',
            )}
          >
            {item.shortLabel ? (
              <>
                <span className="md:tw-hidden">{item.shortLabel}</span>
                <span className="tw-hidden md:tw-inline">{item.label}</span>
              </>
            ) : (
              item.label
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedTabs;
